"use client";

import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname, useRouter } from "apps/website/i18n/navigation";
import { usePostHog } from "posthog-js/react";
import { useCookies } from "react-cookie";
import dayjs from "dayjs";
import { CookieSetOptions } from "universal-cookie";
import { useAuth, useDatadog } from "@auth/client-sdk-react";
import {
  fbcFromFbclidQueryParam,
  TCapiAddToCartCustomData,
  TCapiAddToCartUserData,
} from "@capi/types";
import { IPlanSummaryResponse } from "@forms/schema";
import {
  ITikTokEventTrackBaseRequest,
  TikTokEventEnum,
  TikTokEventSourceEnum,
} from "@api-clients/tiktok";

import { useAPI } from "../hooks/useAPI";
import { PublicConfig } from "../config/public";
import { useAnalytics } from "../hooks/useAnalytics";
import { getFormNameFromPath } from "../utils/abandoned-cart/formNameConfig";
import { persistGclidFromUrl } from "../utils/tracking/persistGclidFromUrl";

type TrackPageViewContext = {
  trackPageView(): void;
  addToCart(data: {
    planSummaryResponse: IPlanSummaryResponse,
    customerId: string | undefined,
    customerEmail: string | undefined,
    customerFirstName: string | undefined,
    customerLastName: string | undefined,
    postcode: string | undefined,
    city: string | undefined,
    phoneNumber: string | undefined,
    formName: string,
    gtmDataEvent: "saw_pricing_page" | "added_nibbles",
  }): Promise<void>;
  checkoutInitiated(data: {
    planSummaryResponse: IPlanSummaryResponse,
    customerId: string,
    customerEmail: string | undefined,
    customerFirstName: string | undefined,
    customerLastName: string | undefined,
    postcode: string | undefined,
    city: string | undefined,
    phoneNumber: string | undefined,
    formName: string,
  }): Promise<void>;
  quizStarted(): Promise<void>;
  lastPageLogged: string | undefined;
  pageLoggedAt: number;
};

const trackPageViewContext = createContext<TrackPageViewContext>(undefined as unknown as TrackPageViewContext);

export const useTrackPageView = () => useContext(trackPageViewContext);

function cookieDomainSettings(
  expires?: dayjs.Dayjs | undefined,
  maxAge?: number | undefined,
): CookieSetOptions {
  const cookieDomain = PublicConfig.NEXT_PUBLIC_COOKIE_DOMAIN;
  const secure = cookieDomain !== "localhost";
  return {
    path: "/",
    domain: cookieDomain,
    secure,
    sameSite: secure ? "none" : "strict",
    expires: expires?.toDate(),
    maxAge,
  };
}

function randomInteger(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

const VAT_RATE_PERCENT = 20;
const VAT_INCLUSIVE_DIVISOR = 120;

// 90 days, matching Google's conversion window
const GCLID_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

const V1_QUIZ_START_PATHS: ReadonlySet<string> = new Set([
  "/forms/fresh-new-user",
  "/forms/fresh-feast-trial",
]);

function buildUserData(params: {
  customerEmail: string | undefined;
  customerFirstName: string | undefined;
  customerLastName: string | undefined;
  postcode: string | undefined;
  phoneNumber: string | undefined;
  fallbackEmail: string | undefined;
  fallbackFirstName: string | undefined;
  fallbackLastName: string | undefined;
}) {
  return {
    email: params.customerEmail?.trim() || params.fallbackEmail?.trim() || undefined,
    firstName: params.customerFirstName?.trim() || params.fallbackFirstName?.trim() || undefined,
    lastName: params.customerLastName?.trim() || params.fallbackLastName?.trim() || undefined,
    postcode: params.postcode?.trim() || undefined,
    phone: params.phoneNumber?.trim() || undefined,
  };
}

function toRequiredString(value: string | undefined): string {
  return value ?? "";
}

function toOptionalTrimmed(value: string | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value.trim();
}

function toOptionalLowerTrimmed(value: string | undefined): string | undefined {
  const trimmed = toOptionalTrimmed(value);
  return trimmed?.toLowerCase();
}

function toRequiredLowerTrimmed(value: string | undefined): string {
  return toRequiredString(value).trim().toLowerCase();
}

function toOptionalLowerNoWhitespace(value: string | undefined): string | undefined {
  const normalized = toOptionalLowerTrimmed(value);
  return normalized?.replaceAll(/\s+/g, "");
}

function toOptionalExternalIds(customerId: string | undefined): string[] | undefined {
  if (!customerId) {
    return undefined;
  }
  return [ customerId ];
}

function buildTikTokInitiateCheckoutEvent(params: {
  eventId: string;
  customerId: string;
  userData: ReturnType<typeof buildUserData>;
  capiCustomData: IPlanSummaryResponse["capiCustomData"];
  ttclid: string | undefined;
  ttp: string | undefined;
}): ITikTokEventTrackBaseRequest {
  const { capiCustomData, userData } = params;
  return {
    event_source: TikTokEventSourceEnum.Web,
    data: [
      {
        event: TikTokEventEnum.InitiateCheckout,
        // Must match the event id of the browser pixel event so TikTok can deduplicate them
        event_id: params.eventId,
        event_time: Math.floor(Date.now() / 1000),
        page: {
          url: globalThis.location.href,
          referrer: globalThis.document?.referrer || undefined,
        },
        user: {
          email: userData.email ? [ userData.email ] : undefined,
          phone: userData.phone ? [ userData.phone ] : undefined,
          external_id: params.customerId,
          ttclid: params.ttclid,
          ttp: params.ttp,
          user_agent: globalThis.navigator?.userAgent || undefined,
        },
        properties: capiCustomData && {
          currency: capiCustomData.currency,
          value: capiCustomData.value,
          content_type: "product",
          contents: capiCustomData.contents?.map((item) => ({
            content_id: item.id,
            content_name: item.title,
            content_category: item.category,
            quantity: item.quantity,
            price: item.itemPrice,
            brand: item.brand,
          })),
        },
      },
    ],
  };
}

function getSourceCookieValue(source: string): "aw" | "other" {
  return source.trim().toLowerCase() === "affiliate" ? "aw" : "other";
}

function buildAddToCartUserData(params: {
  customerId: string | undefined;
  customerEmail: string | undefined;
  customerFirstName: string | undefined;
  customerLastName: string | undefined;
  postcode: string | undefined;
  city: string | undefined;
  phoneNumber: string | undefined;
  fallbackEmail: string | undefined;
  fallbackFirstName: string | undefined;
  fallbackLastName: string | undefined;
  fbc: string | undefined;
  fbp: string | undefined;
}): TCapiAddToCartUserData {
  const userData = buildUserData({
    customerEmail: params.customerEmail,
    customerFirstName: params.customerFirstName,
    customerLastName: params.customerLastName,
    postcode: params.postcode,
    phoneNumber: params.phoneNumber,
    fallbackEmail: params.fallbackEmail,
    fallbackFirstName: params.fallbackFirstName,
    fallbackLastName: params.fallbackLastName,
  });

  return {
    email: toRequiredLowerTrimmed(userData.email),
    firstName: toRequiredLowerTrimmed(userData.firstName),
    lastName: toRequiredLowerTrimmed(userData.lastName),
    country: "gb",
    postcode: toOptionalLowerNoWhitespace(userData.postcode),
    city: toOptionalLowerNoWhitespace(params.city),
    phone: toOptionalTrimmed(userData.phone),
    clientIpAddress: undefined,
    externalIds: toOptionalExternalIds(params.customerId),
    fbc: params.fbc,
    fbp: params.fbp,
    clientUserAgent: globalThis.navigator?.userAgent || undefined,
  };
}

function mapToAddToCartContent(
  item: NonNullable<NonNullable<IPlanSummaryResponse["capiCustomData"]>["contents"]>[number],
  index: number,
): TCapiAddToCartCustomData["contents"][number] {
  const fallbackId = `product-${index}`;
  const id = item.id ?? fallbackId;
  const title = item.title ?? id;

  return {
    id,
    quantity: item.quantity ?? 1,
    itemPrice: item.itemPrice ?? 0,
    title,
    description: item.description ?? title,
    category: item.category ?? "fresh",
    brand: item.brand ?? "katkin",
    deliveryCategory: "home_delivery",
  };
}

function hasRequiredAddToCartCustomData(
  capiCustomData: IPlanSummaryResponse["capiCustomData"],
  contents: TCapiAddToCartCustomData["contents"],
): capiCustomData is IPlanSummaryResponse["capiCustomData"] & {
  value: number;
  currency: string;
  contentName: string;
  contentCategory: string;
} {
  if (!capiCustomData || !contents.length) {
    return false;
  }
  if (typeof capiCustomData.value !== "number") {
    return false;
  }
  if (!capiCustomData.currency || !capiCustomData.contentName || !capiCustomData.contentCategory) {
    return false;
  }
  return true;
}

function buildAddToCartCustomData(
  capiCustomData: IPlanSummaryResponse["capiCustomData"],
): TCapiAddToCartCustomData | undefined {
  if (!capiCustomData) {
    return undefined;
  }

  const contents = capiCustomData.contents?.map(mapToAddToCartContent) ?? [];

  if (!hasRequiredAddToCartCustomData(capiCustomData, contents)) {
    return undefined;
  }

  return {
    value: capiCustomData.value,
    currency: capiCustomData.currency,
    contentName: capiCustomData.contentName,
    contentCategory: capiCustomData.contentCategory,
    contents,
    numItems: capiCustomData.numItems ?? contents.length,
    deliveryCategory: "home_delivery",
    searchString: capiCustomData.searchString,
  };
}

function pushAddToCartToDataLayer({
  gtmDataEvent,
  eventId,
  customerId,
  finalEmail,
  finalFirstName,
  finalLastName,
  finalPostcode,
  finalPhone,
  customData,
  formName,
  datadog,
}: {
  gtmDataEvent: "saw_pricing_page" | "added_nibbles";
  eventId: string;
  customerId: string | undefined;
  finalEmail: string;
  finalFirstName: string | undefined;
  finalLastName: string | undefined;
  finalPostcode: string | undefined;
  finalPhone: string | undefined;
  customData: TCapiAddToCartCustomData;
  formName: string;
  datadog: ReturnType<typeof useDatadog> | undefined;
}): void {
  if (!globalThis.window?.dataLayer) {
    return;
  }

  const gtmData = {
    event: gtmDataEvent,
    customerEmail: finalEmail || undefined,
    customerFirstName: finalFirstName || undefined,
    customerLastName: finalLastName || undefined,
    customerPostcode: finalPostcode,
    customerPhone: finalPhone,
    katkinCustomerId: customerId,
    kkcoupon: customData.searchString,
    kktransactionId: eventId,
    kkrevenue: customData.value.toFixed(2),
    ecommerce: {
      userId: customerId,
      currencyCode: customData.currency,
      add: {
        products: customData.contents.map((item) => ({
          name: item.title,
          id: item.id,
          productId: item.id,
          price: item.itemPrice,
          brand: item.brand,
          category: item.category,
          quantity: item.quantity,
        })),
      },
    },
  };

  try {
    datadog?.logger?.info("WebsiteTracking.addToCart window.dataLayer.push", {
      gtmDataEvent,
      formName,
      gtmData,
    });
    globalThis.window.dataLayer.push({ ecommerce: null }, gtmData);
  } catch (err) {
    console.error("Failed to track addToCart");
    console.error(err);
  }
}

function pushCheckoutToDataLayer({
  eventId,
  customerId,
  listId,
  finalEmail,
  finalFirstName,
  finalLastName,
  finalPostcode,
  finalPhone,
  capiCustomData,
  formName,
  datadog,
}: {
  eventId: string;
  customerId: string;
  listId: string;
  finalEmail: string | undefined;
  finalFirstName: string | undefined;
  finalLastName: string | undefined;
  finalPostcode: string | undefined;
  finalPhone: string | undefined;
  capiCustomData: IPlanSummaryResponse["capiCustomData"];
  formName: string;
  datadog: ReturnType<typeof useDatadog> | undefined;
}): void {
  if (!globalThis.window?.dataLayer) {
    return;
  }

  const gtmDataEvent = `rc_rechargeCheckout_${listId}`;
  const gtmData = {
    event: gtmDataEvent,
    customerEmail: finalEmail,
    customerFirstName: finalFirstName,
    customerLastName: finalLastName,
    customerPostcode: finalPostcode,
    customerPhone: finalPhone,
    katkinCustomerId: customerId,
    kkcoupon: capiCustomData?.searchString,
    kktransactionId: eventId,
    kkrevenue: capiCustomData?.value?.toFixed(2),
    ecommerce: {
      userId: customerId,
      currencyCode: capiCustomData?.currency,
      purchase: {
        actionField: {
          id: eventId,
          affiliation: globalThis.location.hostname,
          revenue: capiCustomData?.value?.toFixed(2),
          // 20% tax calculation
          tax: typeof capiCustomData?.value === "number"
            ? ((capiCustomData.value * VAT_RATE_PERCENT) / VAT_INCLUSIVE_DIVISOR).toFixed(2)
            : undefined,
          shipping: capiCustomData?.contents
            ?.filter((it) => it.id === "Delivery Fee")
            ?.map((it) => it.itemPrice)
            ?.reduce((a, b) => (a || 0) + (b || 0), 0),
          coupon: capiCustomData?.searchString,
          option: capiCustomData?.searchString,
          list: listId,
        },
        products: capiCustomData?.contents?.map((item) => ({
          name: item.id,
          id: item.id,
          productId: item.id,
          price: item.itemPrice,
          brand: item.brand,
          variant: item.id,
          quantity: item.quantity,
        })),
      },
    },
  };

  try {
    datadog?.logger?.info("WebsiteTracking.checkoutInitiated window.dataLayer.push", {
      gtmDataEvent,
      formName,
      gtmData,
    });
    globalThis.window.dataLayer.push({ ecommerce: null }, gtmData);
  } catch (err) {
    console.error("Failed to track checkoutInitiated");
    console.error(err);
  }
}

export const WebsiteTracking: FC<PropsWithChildren> = ({
  children,
}) => {

  const router = useRouter();
  const pathname = usePathname();
  const analytics = useAnalytics();

  const lastTrackedPathRef = useRef<string>(undefined);
  const lastCheckoutInitiatedRef = useRef<string>(undefined);
  const lastQuizStartedRef = useRef<string>(undefined);
  const lastAddToCartRef = useRef<Set<string>>(new Set());

  const {
    firstName,
    lastName,
    email,
    iold,
    old,
    id,
    loggedIn,
    accessToken,
  } = useAuth();

  const posthog = usePostHog();
  const datadog = useDatadog();
  const api = useAPI();
  const [ cookies, setCookieRaw ] = useCookies([
    "_fbp",
    "_fbc",
    "gclid",
    "ttclid",
    "_ttp",
    "logged_in_v1",
    "manage_plan_v2",
    "signup_flow_variant_v2",
    "kk_source",
  ]);

  const setCookie = useCallback<typeof setCookieRaw>(
    (name, value, options) => setCookieRaw(name, value, options),
    [ setCookieRaw ],
  );

  useEffect(() => {

    if (loggedIn) {
      posthog.identify(
        iold ?? old ?? id,
        {
          email,
          firstName,
          lastName,
        },
      );
      const userIdForAnalytics = iold ?? old;
      if (userIdForAnalytics) {
        void analytics.identify(userIdForAnalytics, {
          email,
          name: `${firstName} ${lastName}`,
        }).catch((e) => {
          console.error("Failed to identify user", e);
        });
      }
      if (email) datadog?.setGlobalContextProperty("kk_email", email);
      if (iold) datadog?.setGlobalContextProperty("kk_customer_id", iold || old);
      if (id) datadog?.setGlobalContextProperty("token_sub", id);
    }
    // NOTE: We do not call posthog resets and clearGlobalContext here, because
    // the useUpdateEffect might just run prematurely before full auth hydration
    // Those resets are handled in a callback to the auth context provider instead
  }, [
    email,
    firstName,
    lastName,
    old,
    iold,
    posthog,
    datadog,
    id,
    loggedIn,
  ]);

  const [ lastPageLogged, setLastPageLogged ] = useState<string | undefined>();
  const [ pageLoggedAt, setPageLoggedAt ] = useState<number>(Date.now());
  const capiPageview = useCallback(() => {
    if (loggedIn === undefined || lastPageLogged === pathname) {
      return;
    }

    let fbp = cookies._fbp?.toString()?.trim();
    // Generate an _fbp cookie if it does not exist
    if (!fbp) {
      fbp = `fb.1.${Date.now()}.${randomInteger(1000000000, 2147483647)}`;
      setCookie(
        "_fbp",
        fbp,
        // Keep for 90 days
        cookieDomainSettings(
          undefined,
          90 * 24 * 60 * 60,
        ),
      );
    }

    // Gets the fbclid query parameter and moves it to the _fbc cookie

    const fbcResult = fbcFromFbclidQueryParam(globalThis.location.href, console.error);
    // If we found an fbc query parameter
    if (fbcResult) {
      // And either the fbc cookie is not set
      if (!cookies._fbc
        // Or the fbc cookie is set but has a different (most likely older) fbclid
        || !cookies._fbc.toString().endsWith(fbcResult.fbclidQuery)) {
        setCookie(
          "_fbc",
          fbcResult.fbc,
          // Set as a session cookie
          cookieDomainSettings(),
        );
      }
      // Regardless of whether we set the fbc cookie, we should still remove the fbclid query parameter
      // (actually, don't remove it, since other analytics tools might want to read it, it should be removed anyway
      // when we navigate to another page)
      // fbcResult.parsedUrl.searchParams.delete("fbclid");
      // const updatedUrl = fbcResult.parsedUrl.href;
      // window.history.replaceState({ path: updatedUrl }, "", updatedUrl);
    }

    setLastPageLogged(pathname);
    setPageLoggedAt(Date.now());
    void api.Capi.pageview(globalThis.location.href, accessToken, {
      fbc: fbcResult?.fbc || cookies._fbc,
      fbp: fbp || cookies._fbp,
      email,
      firstName,
      lastName,
      externalIds: old ? [ old ] : undefined,
    }, getFormNameFromPath(globalThis.location.pathname), getCapiPageviewEventId()).catch(
      // Don't let tracking failures crash the app
      () => { },
    );
  }, [
    lastPageLogged,
    accessToken,
    loggedIn,
    pathname,
    setCookie,
    api.Capi,
    email,
    firstName,
    lastName,
    old,
  ]);

  // Global-level hook which sets up Logged in Experience V1 routing cookie
  useEffect(() => {
    // Already been given a group
    if (cookies.manage_plan_v2 !== undefined) {
      return;
    }

    posthog.onFeatureFlags(() => {
      if (posthog.isFeatureEnabled("manage-plan-v2-routing")) {
        setCookie("manage_plan_v2", true);
      } else {
        setCookie("manage_plan_v2", false);
      }

    });
  }, [ posthog, router, cookies.manage_plan_v2, setCookie ]);

  const quizStarted: TrackPageViewContext["quizStarted"] = useCallback(async () => {
    if (process.env.NODE_ENV !== "production") { return; }
    if (loggedIn === undefined) { return; }

    const currentPathname = globalThis.location.pathname;
    const currentUrl = globalThis.location.href;
    const eventId = `qs.1.${currentPathname}.${pageLoggedAt}`;

    if (lastQuizStartedRef.current === eventId) {
      datadog?.logger?.info("[quizStarted] BLOCKED - already fired", { eventId, pathname: currentPathname });
      return;
    }
    lastQuizStartedRef.current = eventId;
    datadog?.logger?.info("[quizStarted] FIRING", { eventId, pathname: currentPathname });

    const fbcResult = fbcFromFbclidQueryParam(currentUrl, console.error);

    try {
      await api.Capi.quizStarting(
        currentUrl,
        {
          fbc: fbcResult?.fbc || cookies._fbc,
          fbp: cookies._fbp,
          email,
          firstName,
          lastName,
          externalIds: old ? [ old ] : undefined,
        },
        getFormNameFromPath(currentPathname),
        eventId,
      );
    } catch (err) {
      datadog?.logger?.error(
        "[quizStarted] CAPI failed",
        { eventId, pathname: currentPathname },
        err as Error,
      );
    }
  }, [
    api.Capi,
    cookies._fbc,
    cookies._fbp,
    datadog,
    email,
    firstName,
    lastName,
    loggedIn,
    old,
    pageLoggedAt,
  ]);

  const trackPageView = useCallback(() => {
    if (process.env.NODE_ENV !== "production") { return; }
    if (lastTrackedPathRef.current === pathname) { return; }
    datadog?.logger?.info("[trackPageView]", {
      currentTracked: pathname,
      lastTracked: lastTrackedPathRef.current,
      formName: getFormNameFromPath(pathname) ?? "NO_FORM",
    });
    lastTrackedPathRef.current = pathname;
    const searchParams = new URLSearchParams(globalThis.window.location.search);
    const utmSource = searchParams.get("utm_source");
    if (utmSource) {
      const sourceValue = getSourceCookieValue(utmSource);
      setCookie(
        "kk_source",
        sourceValue,
        cookieDomainSettings(undefined, 30 * 24 * 60 * 60), // (30 days)
      );
    }
    posthog.capture("$pageview", {
      signup_variant: cookies.signup_flow_variant_v2,
    });
    void analytics.page();

    // Capture the Google Ads gclid into a cookie here, ahead of the
    // auth-dependent capiPageview guard. capiPageview early-returns while
    // `loggedIn` is undefined, and this path is already marked tracked above,
    // so capturing inside it would miss a first ad landing with unresolved
    // auth. By checkout the `?gclid=` query param is usually gone, so we must
    // persist it on the landing pageview regardless of auth state.
    persistGclidFromUrl({
      url: globalThis.location.href,
      currentGclid: cookies.gclid,
      setCookie,
      cookieOptions: cookieDomainSettings(undefined, GCLID_COOKIE_MAX_AGE_SECONDS),
      logger: console.error,
    });

    void capiPageview();

    if (V1_QUIZ_START_PATHS.has(pathname)) {
      void quizStarted();
    }
  }, [
    analytics,
    posthog,
    capiPageview,
    pathname,
    datadog,
    cookies.signup_flow_variant_v2,
    cookies.gclid,
    quizStarted,
    setCookie,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") { return; }

    // Track the initial page view
    void trackPageView();

  }, [ trackPageView, pathname ]);

  const addToCart: TrackPageViewContext["addToCart"] = useCallback(async ({
    planSummaryResponse,
    customerId,
    customerEmail,
    customerFirstName,
    customerLastName,
    postcode,
    city,
    phoneNumber,
    formName,
    gtmDataEvent,
  }): Promise<void> => {
    if (process.env.NODE_ENV !== "production") { return; }

    const eventIdCustomer = customerId ?? cookies._fbp ?? "no_fbp";
    const eventId = `${gtmDataEvent}_${eventIdCustomer}_${pageLoggedAt}`;
    if (lastAddToCartRef.current.has(eventId)) {
      datadog?.logger?.info("[addToCart] BLOCKED - already fired", { eventId, customerId, gtmDataEvent });
      return;
    }

    const customData = buildAddToCartCustomData(planSummaryResponse.capiCustomData);
    if (!customData) {
      datadog?.logger?.warn("[addToCart] BLOCKED - missing/invalid customData", {
        customerId,
        gtmDataEvent,
        formName,
      });
      return;
    }

    const fbc = fbcFromFbclidQueryParam(globalThis.location.href, console.error)?.fbc || cookies._fbc;
    const userData = buildAddToCartUserData({
      customerId,
      customerEmail,
      customerFirstName,
      customerLastName,
      postcode,
      city,
      phoneNumber,
      fallbackEmail: email,
      fallbackFirstName: firstName,
      fallbackLastName: lastName,
      fbc,
      fbp: cookies._fbp,
    });

    lastAddToCartRef.current.add(eventId);
    datadog?.logger?.info("[addToCart] FIRING", { eventId, customerId, gtmDataEvent, formName });

    try {
      await api.Capi.addToCart(
        globalThis.location.href,
        userData,
        formName,
        customData,
        eventId,
      );
    } catch (error) {
      datadog?.logger?.error("[addToCart] CAPI failed - continuing with GTM push", {
        eventId,
        customerId,
        gtmDataEvent,
        formName,
      }, error as Error);
    }

    pushAddToCartToDataLayer({
      gtmDataEvent,
      eventId,
      customerId,
      finalEmail: userData.email,
      finalFirstName: userData.firstName,
      finalLastName: userData.lastName,
      finalPostcode: userData.postcode,
      finalPhone: userData.phone,
      customData,
      formName,
      datadog,
    });
  }, [
    api.Capi,
    cookies._fbc,
    cookies._fbp,
    datadog,
    email,
    firstName,
    lastName,
    pageLoggedAt,
  ]);

  const checkoutInitiated: TrackPageViewContext["checkoutInitiated"] = useCallback(async ({
    planSummaryResponse,
    customerId,
    customerEmail,
    customerFirstName,
    customerLastName,
    postcode,
    city,
    phoneNumber,
    formName,
  }): Promise<void> => {
    if (process.env.NODE_ENV !== "production") { return; }
    const eventId = `${customerId}_${pageLoggedAt}`;

    // Guard: prevent firing more than once per customer per session
    if (lastCheckoutInitiatedRef.current === eventId) {
      datadog?.logger?.info("[checkoutInitiated] BLOCKED - already fired", { eventId, customerId });
      return;
    }
    lastCheckoutInitiatedRef.current = eventId;
    datadog?.logger?.info("[checkoutInitiated] FIRING", { eventId, customerId });

    const userData = buildUserData({
      customerEmail,
      customerFirstName,
      customerLastName,
      postcode,
      phoneNumber,
      fallbackEmail: email,
      fallbackFirstName: firstName,
      fallbackLastName: lastName,
    });
    // Sent in parallel with CAPI, and never throws so TikTok problems can't affect the rest of the checkout tracking
    const tiktokEvent = api.TikTok.trackEvent(buildTikTokInitiateCheckoutEvent({
      eventId,
      customerId,
      userData,
      capiCustomData: planSummaryResponse.capiCustomData,
      ttclid: cookies.ttclid?.toString(),
      ttp: cookies._ttp?.toString(),
    })).catch((error: unknown) => {
      datadog?.logger?.warn("[checkoutInitiated] Failed to send TikTok InitiateCheckout event", {
        eventId,
        customerId,
      }, error as Error);
    });
    await api.Capi.initiateCheckout(
      globalThis.location.href,
      accessToken,
      eventId,
      planSummaryResponse.capiCustomData,
      formName,
      {
        fbc: fbcFromFbclidQueryParam(globalThis.location.href, console.error)?.fbc || cookies._fbc,
        fbp: cookies._fbp,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        externalIds: [ customerId ],
        postcode: userData.postcode,
        city: city?.trim() || undefined,
        phone: userData.phone,
      },
    );
    const listId = `${planSummaryResponse.capiCustomData?.contentCategory}_${planSummaryResponse.capiCustomData?.contentName}`;
    pushCheckoutToDataLayer({
      eventId,
      customerId,
      listId,
      finalEmail: userData.email,
      finalFirstName: userData.firstName,
      finalLastName: userData.lastName,
      finalPostcode: userData.postcode,
      finalPhone: userData.phone,
      capiCustomData: planSummaryResponse.capiCustomData,
      formName,
      datadog,
    });
    await tiktokEvent;
  }, [
    accessToken,
    api.Capi,
    api.TikTok,
    cookies._fbc,
    cookies._fbp,
    cookies.ttclid,
    cookies._ttp,
    datadog,
    email,
    firstName,
    lastName,
    pageLoggedAt,
  ]);

  const context = useMemo<TrackPageViewContext>(() => ({
    trackPageView,
    addToCart,
    lastPageLogged,
    pageLoggedAt,
    checkoutInitiated,
    quizStarted,
  }), [ addToCart, lastPageLogged, pageLoggedAt, trackPageView, checkoutInitiated, quizStarted ]);

  return (
    <trackPageViewContext.Provider value={context}>
      { children }
    </trackPageViewContext.Provider>
  );
};

declare global {
  interface Window {
    _kkGtmFBPageViewEventId?: string;
    _kkGtmFBPageViewLastLoaded?: string;
  }
}

const MIN_PV_EVENT_ID = 1000000000;
const MAX_PV_EVENT_ID = 2147483647;

/** Generates a random eventId for use in pageview events.
 * The eventId is stored in a global value so that it's available
 * to GTM. If GTM happens to run first for whatever reason,
 * it runs the same function, so we can instead use the eventId it generated. */
function getCapiPageviewEventId() {
  if ((globalThis as unknown as Window)._kkGtmFBPageViewEventId
    && (globalThis as unknown as Window)._kkGtmFBPageViewLastLoaded === globalThis.location.pathname) {
    return (globalThis as unknown as Window)._kkGtmFBPageViewEventId;
  }

  const eventId = `pv.1.${Date.now()
  }.${randomInteger(MIN_PV_EVENT_ID, MAX_PV_EVENT_ID)
  }`;

  (globalThis as unknown as Window)._kkGtmFBPageViewEventId = eventId;
  (globalThis as unknown as Window)._kkGtmFBPageViewLastLoaded = globalThis.location.pathname;

  return eventId;
}
